/**
 * Image compression utility for reducing file size before upload
 */

interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  mimeType?: 'image/jpeg' | 'image/webp';
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxWidth: 1280,
  maxHeight: 1280,
  quality: 0.7,
  mimeType: 'image/jpeg'
};

const TARGET_ASPECT_RATIO = 9 / 16; // Portrait mode like phone selfie

/**
 * Compresses an image blob to reduce file size
 * @param imageBlob - The original image blob
 * @param options - Compression options
 * @returns Promise<Blob> - The compressed image blob
 */
export async function compressImage(
  imageBlob: Blob,
  options: CompressionOptions = {}
): Promise<Blob> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(imageBlob);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      const maxWidth = opts.maxWidth!;
      const maxHeight = opts.maxHeight!;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      
      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      
      // Use better image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to blob with compression
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        opts.mimeType,
        opts.quality
      );
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
}

/**
 * Adds watermark to an image canvas
 * @param canvas - The canvas element
 * @param dateTime - The date/time string to watermark
 * @param quote - Optional motivational quote
 */
export function addWatermark(
  canvas: HTMLCanvasElement,
  dateTime: string,
  quote?: string
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const hasQuote = Boolean(quote && quote.trim().length > 0);
  const quoteText = hasQuote ? quote!.trim() : '';
  
  // Scale font sizes based on image width for consistent appearance
  const scaleFactor = canvas.width / 1080; // Base scale on 1080px width
  const paddingOuter = Math.round(40 * scaleFactor);
  const innerPadding = Math.round(30 * scaleFactor);
  const quoteFontSize = hasQuote ? Math.round(72 * scaleFactor) : 0; // Large quote
  const dateFontSize = Math.round(36 * scaleFactor); // Smaller date

  ctx.textBaseline = 'alphabetic';

  // Measure text widths
  let quoteWidth = 0;
  let quoteLineHeight = 0;

  if (hasQuote) {
    ctx.font = `900 ${quoteFontSize}px "Segoe UI", Arial, sans-serif`;
    quoteWidth = ctx.measureText(quoteText).width;
    quoteLineHeight = quoteFontSize + Math.round(16 * scaleFactor);
  }

  ctx.font = `700 ${dateFontSize}px "Segoe UI", Arial, sans-serif`;
  const dateWidth = ctx.measureText(dateTime).width;
  const dateLineHeight = dateFontSize + Math.round(12 * scaleFactor);

  const contentWidth = Math.max(quoteWidth, dateWidth);
  const contentHeight = (hasQuote ? quoteLineHeight : 0) + dateLineHeight;
  const backgroundWidth = contentWidth + innerPadding * 2;
  const backgroundHeight = contentHeight + innerPadding * 2;
  const backgroundX = paddingOuter;
  const backgroundY = canvas.height - backgroundHeight - paddingOuter;

  // Draw background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.fillRect(backgroundX, backgroundY, backgroundWidth, backgroundHeight);

  // Draw quote and date/time text
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
  ctx.lineWidth = Math.round(6 * scaleFactor);
  ctx.fillStyle = '#FFFFFF';

  let textY = backgroundY + innerPadding;
  const textX = backgroundX + innerPadding;

  if (hasQuote) {
    ctx.font = `900 ${quoteFontSize}px "Segoe UI", Arial, sans-serif`;
    textY += quoteFontSize;
    ctx.strokeText(quoteText, textX, textY);
    ctx.fillText(quoteText, textX, textY);
    textY += quoteLineHeight - quoteFontSize;
  }

  ctx.font = `700 ${dateFontSize}px "Segoe UI", Arial, sans-serif`;
  textY += dateFontSize;
  ctx.strokeText(dateTime, textX, textY);
  ctx.fillText(dateTime, textX, textY);
}

/**
 * Creates a watermarked and compressed image from a video frame
 * @param videoElement - The video element to capture from
 * @param dateTime - The date/time string for watermark
 * @param quote - Optional motivational quote
 * @param compressionOptions - Optional compression settings
 * @returns Promise<Blob> - The final compressed image blob
 */
export async function captureAndProcessImage(
  videoElement: HTMLVideoElement,
  dateTime: string,
  quote?: string,
  compressionOptions?: CompressionOptions
): Promise<Blob> {
  const rawWidth = videoElement.videoWidth;
  const rawHeight = videoElement.videoHeight;

  if (!rawWidth || !rawHeight) {
    throw new Error('Video stream not ready');
  }

  // Determine crop area to enforce 16:9 aspect ratio
  const sourceRatio = rawWidth / rawHeight;
  let sourceWidth = rawWidth;
  let sourceHeight = rawHeight;
  let sourceX = 0;
  let sourceY = 0;

  if (Math.abs(sourceRatio - TARGET_ASPECT_RATIO) > 0.001) {
    if (sourceRatio > TARGET_ASPECT_RATIO) {
      // Too wide - crop horizontally
      sourceWidth = rawHeight * TARGET_ASPECT_RATIO;
      sourceX = (rawWidth - sourceWidth) / 2;
    } else {
      // Too tall - crop vertically
      sourceHeight = rawWidth / TARGET_ASPECT_RATIO;
      sourceY = (rawHeight - sourceHeight) / 2;
    }
  }

  sourceWidth = Math.round(sourceWidth);
  sourceHeight = Math.round(sourceHeight);
  sourceX = Math.round(sourceX);
  sourceY = Math.round(sourceY);

  // Create canvas from video with enforced aspect ratio
  const canvas = document.createElement('canvas');
  canvas.width = sourceWidth;
  canvas.height = sourceHeight;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }
  
  // Draw video frame (mirror for selfie effect)
  ctx.translate(canvas.width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(
    videoElement,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvas.width,
    canvas.height
  );
  
  // Reset transform for watermark
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  
  // Add watermark
  addWatermark(canvas, dateTime, quote);
  
  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          reject(new Error('Failed to capture image'));
          return;
        }
        
        try {
          // Compress the image
          const compressed = await compressImage(blob, compressionOptions);
          resolve(compressed);
        } catch (error) {
          reject(error);
        }
      },
      'image/jpeg',
      0.9
    );
  });
}

/**
 * Process an image file from native camera input
 * Adds watermark and compresses the image
 * @param file - The image file from input
 * @param dateTime - The date/time string for watermark
 * @param quote - Optional motivational quote
 * @param compressionOptions - Optional compression settings
 * @returns Promise<Blob> - The processed and compressed image blob
 */
export async function processImageFromFile(
  file: File,
  dateTime: string,
  quote?: string,
  compressionOptions?: CompressionOptions
): Promise<Blob> {
  const opts = { ...DEFAULT_OPTIONS, ...compressionOptions };

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Calculate dimensions while maintaining aspect ratio
      let { width, height } = img;
      const maxWidth = opts.maxWidth!;
      const maxHeight = opts.maxHeight!;

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Create canvas and draw image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Use better image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      // Add watermark
      addWatermark(canvas, dateTime, quote);

      // Convert to blob with compression
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to process image'));
          }
        },
        opts.mimeType,
        opts.quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}
